// Bộ icon dùng chung cho thanh điều hướng (TopNav / BottomNav) và mọi nơi cần
// nhắc lại đúng biểu tượng của từng tính năng (VD: Lịch sử). Giữ ở một chỗ để
// icon không bị lệch giữa các màn.
const ICON_SIZE = 22;

type NavIconProps = { className?: string };
type ToggleNavIconProps = NavIconProps & { active?: boolean };

// ── Trang chủ: Trăng lưỡi liềm ôm ngôi sao thiên hà ────────────────────────
export function IconTrangChu({ className, active = false }: ToggleNavIconProps) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className={className}>
      <path
        d="M12 3a9 9 0 0 0 9 9c0 .46-.04.92-.1 1.36a7.5 7.5 0 1 1-7.64-7.64c.44-.06.9-.1 1.36-.1c-.54 0-1.07.03-1.62.1z"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={active ? "currentColor" : "none"}
        fillOpacity={0.15}
      />
      <path d="M16.5 5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7.7-1.8z" fill="currentColor" />
    </svg>
  );
}

// ── Lá số: Bàn mệnh 12 cung thu gọn ─────────────────────────────────────────
export function IconLaSo({ className, active = false }: ToggleNavIconProps) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className={className}>
      <rect
        x="3.5"
        y="3.5"
        width="17"
        height="17"
        rx="2.5"
        stroke="currentColor"
        strokeWidth={1.8}
        fill={active ? "currentColor" : "none"}
        fillOpacity={0.1}
      />
      <path
        d="M9 3.8v5.1H3.8M15 3.8v5.1h5.2M9 20.2v-5.1H3.8M15 20.2v-5.1h5.2"
        stroke="currentColor"
        strokeWidth={1.35}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2.1" stroke="currentColor" strokeWidth={1.45} />
      <circle cx="12" cy="12" r="0.65" fill="currentColor" />
    </svg>
  );
}

// ── Chòm Sao (Tử Vi) ───────────────────────────────────────────────────────
export function IconChomSao({ className }: NavIconProps) {
  const size = ICON_SIZE;
  const stars = [
    { x: size * 0.5, y: size * 0.12 },
    { x: size * 0.78, y: size * 0.3 },
    { x: size * 0.68, y: size * 0.62 },
    { x: size * 0.3, y: size * 0.72 },
    { x: size * 0.14, y: size * 0.42 },
    { x: size * 0.36, y: size * 0.26 },
  ];
  const connections = [
    [0, 1],
    [1, 2],
    [2, 3],
    [3, 4],
    [4, 5],
    [5, 0],
    [5, 1],
  ];
  const dotR = size * 0.055;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      className={className}
    >
      {connections.map(([a, b], index) => (
        <line
          key={index}
          x1={stars[a].x}
          y1={stars[a].y}
          x2={stars[b].x}
          y2={stars[b].y}
          stroke="currentColor"
          strokeWidth={1}
          opacity={0.65}
        />
      ))}
      {stars.map((star, index) => (
        <circle key={index} cx={star.x} cy={star.y} r={dotR} fill="currentColor" />
      ))}
    </svg>
  );
}

// ── Tứ Trụ (Bát Tự) ────────────────────────────────────────────────────────
export function IconTuTru({ className }: NavIconProps) {
  const size = ICON_SIZE;
  const colW = size * 0.12;
  const gap = size * 0.12;
  const totalW = 4 * colW + 3 * gap;
  const startX = (size - totalW) / 2;
  const heights = [size * 0.6, size * 0.72, size * 0.66, size * 0.56];

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      className={className}
    >
      <line
        x1={startX - gap * 0.5}
        y1={size / 2}
        x2={startX + totalW - colW + gap * 0.5}
        y2={size / 2}
        stroke="currentColor"
        strokeWidth={1.2}
        strokeDasharray="2 1.5"
        opacity={0.8}
      />
      {heights.map((colH, index) => {
        const x = startX + index * (colW + gap);
        const y = (size - colH) / 2;

        return (
          <rect
            key={index}
            x={x}
            y={y}
            width={colW}
            height={colH}
            rx={colW * 0.25}
            stroke="currentColor"
            strokeWidth={1.2}
            fill="none"
          />
        );
      })}
    </svg>
  );
}

// ── Se Duyên: hai nhẫn lồng nhau + sợi tơ hồng ──────────────────────────────
export function IconSeDuyen({ className }: NavIconProps) {
  const size = ICON_SIZE;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      className={className}
    >
      <circle
        cx={size * 0.38}
        cy={size * 0.52}
        r={size * 0.24}
        stroke="currentColor"
        strokeWidth={1.5}
      />
      <circle
        cx={size * 0.62}
        cy={size * 0.52}
        r={size * 0.24}
        stroke="currentColor"
        strokeWidth={1.5}
      />
      <path
        d={`M ${size * 0.14} ${size * 0.2} Q ${size * 0.5} ${size * 0.02} ${size * 0.86} ${size * 0.2}`}
        stroke="currentColor"
        strokeWidth={1.2}
        strokeDasharray="2 1.5"
        strokeLinecap="round"
        opacity={0.8}
      />
      <circle cx={size * 0.5} cy={size * 0.52} r={size * 0.045} fill="currentColor" />
    </svg>
  );
}

// ── 3 Đồng Xu Cổ xếp chéo (Kinh Dịch) ──────────────────────────────────────
export function IconDongXu({ className }: NavIconProps) {
  const size = ICON_SIZE;
  const r = size * 0.22;
  const holeHalf = r * 0.38;
  const coins = [
    { cx: size * 0.3, cy: size * 0.62 },
    { cx: size * 0.5, cy: size * 0.42 },
    { cx: size * 0.7, cy: size * 0.62 },
  ];
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      className={className}
    >
      {coins.map((c, i) => (
        <g key={i}>
          <circle cx={c.cx} cy={c.cy} r={r} stroke="currentColor" strokeWidth={1.5} />
          <rect
            x={c.cx - holeHalf}
            y={c.cy - holeHalf}
            width={holeHalf * 2}
            height={holeHalf * 2}
            stroke="currentColor"
            strokeWidth={1.2}
          />
        </g>
      ))}
    </svg>
  );
}

// ── Âm Dương Thái Cực (Bói Đê) ─────────────────────────────────────────────
export function IconAmDuong({ className }: NavIconProps) {
  const size = ICON_SIZE;
  const cx = size / 2;
  const cy = size / 2;
  const R = size * 0.42;
  const r = R / 2;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      className={className}
    >
      <circle cx={cx} cy={cy} r={R} stroke="currentColor" strokeWidth={1.5} />
      <path
        d={`M ${cx} ${cy - R} A ${r} ${r} 0 0 1 ${cx} ${cy} A ${r} ${r} 0 0 0 ${cx} ${cy + R}`}
        stroke="currentColor"
        strokeWidth={1.5}
        fill="none"
      />
      <circle cx={cx} cy={cy - r} r={r * 0.28} fill="currentColor" />
      <circle
        cx={cx}
        cy={cy + r}
        r={r * 0.28}
        stroke="currentColor"
        strokeWidth={1.2}
        fill="none"
      />
    </svg>
  );
}

// ── Microphone / Sóng Âm (Bói Live) ────────────────────────────────────────
export function IconBoiLive({ className }: NavIconProps) {
  return (
    <div className="relative flex size-6 items-center justify-center">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className={className}>
        <rect x="3" y="8" width="2.2" height="8" rx="1.1" />
        <rect x="7.2" y="5.5" width="2.2" height="13" rx="1.1" />
        <rect x="11.4" y="3" width="2.2" height="18" rx="1.1" />
        <rect x="15.6" y="6.5" width="2.2" height="11" rx="1.1" />
        <rect x="19.8" y="9" width="2.2" height="6" rx="1.1" />
      </svg>
    </div>
  );
}

// ── Bói chỉ tay: bàn tay xoè, nổi ba đường chỉ chính ───────────────────────
export function IconChiTay({ className }: NavIconProps) {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" className={className}>
      <path
        d="M7.4 12.4V6.6a1.4 1.4 0 0 1 2.8 0v5M10.2 11V4.9a1.4 1.4 0 0 1 2.8 0V11M13 11V6.9a1.4 1.4 0 0 1 2.8 0v6.3M15.8 13.2v-2.4a1.4 1.4 0 0 1 2.8 0v6.1c0 2.9-2.3 5.2-5.2 5.2h-1.9c-2.5 0-3.9-1.3-4.7-3.4l-1.7-4a1.4 1.4 0 0 1 2.3-1.4l1.8 2.1"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.6 15.4c1.1 1.9 2.6 3 4.5 3.2"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        opacity=".6"
      />
      <path
        d="M8.2 17.6c1.4 1.1 3 1.6 4.7 1.6"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        opacity=".45"
      />
    </svg>
  );
}

// ── Chỉ tay đã lưu: bàn tay nằm trong quyển sổ ─────────────────────────────
export function IconPalmArchive({ className }: NavIconProps) {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" className={className}>
      <path
        d="M6 4.5h10.4A2.6 2.6 0 0 1 19 7.1v12.4H8.6A2.6 2.6 0 0 1 6 16.9V4.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M6 7.5h-1A1.5 1.5 0 0 0 3.5 9v8.1A2.4 2.4 0 0 0 5.9 19.5H19"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M10.2 14.8v-3.1a.9.9 0 0 1 1.8 0v1.5m0-1.5V9.8a.9.9 0 0 1 1.8 0v3.4m0-1.9v-1.1a.9.9 0 0 1 1.8 0v2.5m0-1.3a.9.9 0 0 1 1.8 0v2.5a2.7 2.7 0 0 1-2.7 2.7h-1.1a2.5 2.5 0 0 1-2.3-1.6l-.8-1.9a.9.9 0 0 1 1.5-.8Z"
        stroke="currentColor"
        strokeWidth="1.15"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Bản quét chỉ tay: khung máy soi + các đường chỉ chính ──────────────────
export function IconPalmScan({ className }: NavIconProps) {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" className={className}>
      <path
        d="M4.5 8V5.5h2.7M19.5 8V5.5h-2.7M4.5 16v2.5h2.7M19.5 16v2.5h-2.7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.2 14.5v-3.6a.95.95 0 0 1 1.9 0v1.2m0-1.2V8.8a.95.95 0 0 1 1.9 0v3.3m0-2.4a.95.95 0 0 1 1.9 0v2.4m0-1.2a.95.95 0 0 1 1.9 0v2.3a2.9 2.9 0 0 1-2.9 2.9h-1.1a2.7 2.7 0 0 1-2.5-1.7l-.7-1.7a.95.95 0 0 1 1.5-.9Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.3 14.2c.8.8 1.7 1.2 2.8 1.3m-2.4-2.7c.8.7 1.7 1 2.7 1.1"
        stroke="currentColor"
        strokeWidth=".95"
        strokeLinecap="round"
        opacity=".65"
      />
    </svg>
  );
}

// ── Vận khí: luồng khí lưu chuyển ──────────────────────────────────────────
export function IconVanKhi({ className }: NavIconProps) {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" className={className}>
      <path
        d="M4 9.1c1.2-2.5 3.8-4.2 6.8-4.2 2.5 0 4.2 1.15 4.2 2.75 0 1.35-1.2 2.3-2.75 2.3H6.1"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.2 13.1h13.45c2.25 0 3.85 1.15 3.85 2.9 0 1.7-1.45 3.05-3.35 3.05-1.45 0-2.65-.75-3.2-1.9"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.2 16.3h4.15c1.2 0 2.05.65 2.05 1.55 0 .95-.8 1.65-1.85 1.65-.75 0-1.4-.4-1.75-1"
        stroke="currentColor"
        strokeWidth="1.45"
        strokeLinecap="round"
      />
      <path d="m18.55 3.15.4 1 .95.4-.95.4-.4 1-.4-1-.95-.4.95-.4.4-1Z" fill="currentColor" />
    </svg>
  );
}

// ── Hỏi thầy: lời hỏi trong khung đối thoại ─────────────────────────────────
export function IconHoiThay({ className }: NavIconProps) {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" className={className}>
      <path
        d="M5.3 4.2h13.4a2.8 2.8 0 0 1 2.8 2.8v7.7a2.8 2.8 0 0 1-2.8 2.8h-6.1l-4.8 3v-3H5.3a2.8 2.8 0 0 1-2.8-2.8V7a2.8 2.8 0 0 1 2.8-2.8Z"
        stroke="currentColor"
        strokeWidth="1.45"
        strokeLinejoin="round"
      />
      <path
        d="M11.1 7.1a2.2 2.2 0 0 1 3.75 1.55c0 1.55-1.55 1.85-2.2 2.75-.2.28-.28.58-.28.9"
        stroke="currentColor"
        strokeWidth="1.55"
        strokeLinecap="round"
      />
      <circle cx="12.35" cy="14.75" r=".85" fill="currentColor" />
      <path
        d="m6.4 7.1.35.85.85.35-.85.35-.35.85-.35-.85-.85-.35.85-.35.35-.85Z"
        fill="currentColor"
      />
    </svg>
  );
}

// ── Đoán giờ sinh: mặt đồng hồ có sao dò giờ ───────────────────────────────
export function IconDoanGio({ className }: NavIconProps) {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" className={className}>
      <circle cx="11" cy="13" r="7.6" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M11 8.6V13l2.9 1.9"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11 5.4v.9M18.6 13h-.9M11 20.6v-.9M3.4 13h.9"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <path
        d="m19.2 2.6.45 1.1 1.1.45-1.1.45-.45 1.1-.45-1.1-1.1-.45 1.1-.45.45-1.1Z"
        fill="currentColor"
      />
    </svg>
  );
}

// ── La bàn phong thủy ──────────────────────────────────────────────────────
export function IconLaBan({ className }: NavIconProps) {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" className={className}>
      <circle cx="12" cy="12" r="8.2" stroke="currentColor" strokeWidth="1.45" />
      <circle cx="12" cy="12" r="5.7" stroke="currentColor" strokeWidth="1" opacity=".55" />
      <path
        d="m14.9 8.2-1.55 5.15-5.15 1.55 1.55-5.15 5.15-1.55Z"
        stroke="currentColor"
        strokeWidth="1.45"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
      <path
        d="M12 2.4v1.2M21.6 12h-1.2M12 21.6v-1.2M2.4 12h1.2"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Điểm danh: lịch ngày có ấn xác nhận ────────────────────────────────────
export function IconDiemDanh({ className }: NavIconProps) {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" className={className}>
      <rect
        x="3.5"
        y="5.2"
        width="17"
        height="15"
        rx="2.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M7.5 3.2v4M16.5 3.2v4M3.8 9.2h16.4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="m8.3 14.4 2.3 2.2 5.1-5"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Xem ngày: tờ lịch có ngôi sao đánh dấu ngày đẹp ───────────────────────
export function IconXemNgay({ className }: NavIconProps) {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" className={className}>
      <rect
        x="3.5"
        y="5.2"
        width="17"
        height="15"
        rx="2.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M7.5 3.2v4M16.5 3.2v4M3.8 9.2h16.4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="m12 11.4 1.05 2.13 2.35.34-1.7 1.66.4 2.34L12 16.76l-2.1 1.11.4-2.34-1.7-1.66 2.35-.34z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Hộp thư: phong thư mở với dấu niêm ─────────────────────────────────────
export function IconHopThu({ className }: NavIconProps) {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" className={className}>
      <path
        d="M3.5 8.3 12 3.8l8.5 4.5v10.2a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2V8.3Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="m4 8.7 8 6 8-6M4.1 19.4l5.6-6.3M19.9 19.4l-5.6-6.3"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="14.5" r="1.3" fill="currentColor" />
    </svg>
  );
}

// ── Tài lộc: đồng tiền cổ treo dây cát tường ───────────────────────────────
export function IconTaiLoc({ className }: NavIconProps) {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" className={className}>
      <circle cx="12" cy="10.2" r="6.6" stroke="currentColor" strokeWidth="1.5" />
      <rect
        x="9.7"
        y="7.9"
        width="4.6"
        height="4.6"
        rx=".35"
        stroke="currentColor"
        strokeWidth="1.35"
      />
      <path
        d="M9.2 16.2 7 20.5l5-2.1 5 2.1-2.2-4.3"
        stroke="currentColor"
        strokeWidth="1.45"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Nạp tiền: ví và đồng xu ────────────────────────────────────────────────
export function IconNapTien({ className }: NavIconProps) {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" className={className}>
      <path
        d="M4 6.5h13.4a2.6 2.6 0 0 1 2.6 2.6v9.4a2 2 0 0 1-2 2H4.5a2 2 0 0 1-2-2v-10a2 2 0 0 1 1.5-2Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M4.2 6.5 15.5 3.4l.8 3.1M15.4 11.1h5.1v4.5h-5.1a2.25 2.25 0 0 1 0-4.5Z"
        stroke="currentColor"
        strokeWidth="1.45"
        strokeLinejoin="round"
      />
      <circle cx="15.5" cy="13.35" r=".7" fill="currentColor" />
    </svg>
  );
}

// ── Giới thiệu: hộp quà kết dây may mắn ────────────────────────────────────
export function IconGioiThieu({ className }: NavIconProps) {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" className={className}>
      <path
        d="M3.2 9.3h17.6v3.5H3.2V9.3ZM4.7 12.8h14.6v7.7H4.7v-7.7Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M12 9.2v11.2M7 9.1C4.6 7.3 5.8 4.3 8.1 4.6c2 .25 3.2 2.6 3.9 4.5M17 9.1c2.4-1.8 1.2-4.8-1.1-4.5-2 .25-3.2 2.6-3.9 4.5"
        stroke="currentColor"
        strokeWidth="1.45"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Hướng dẫn: sách cổ mở trang ────────────────────────────────────────────
export function IconHuongDan({ className }: NavIconProps) {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" className={className}>
      <path
        d="M3.5 5.2c3.5-.8 6.4.1 8.5 2.1v12.2c-2.1-2-5-2.9-8.5-2.1V5.2ZM20.5 5.2c-3.5-.8-6.4.1-8.5 2.1v12.2c2.1-2 5-2.9 8.5-2.1V5.2Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.4 8.4c1.3-.1 2.4.15 3.3.7M6.4 11.3c1.3-.1 2.4.15 3.3.7M17.6 8.4c-1.3-.1-2.4.15-3.3.7M17.6 11.3c-1.3-.1-2.4.15-3.3.7"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinecap="round"
        opacity=".7"
      />
    </svg>
  );
}

// ── Xem thêm: bốn ấn vuông đồng bộ nét thương hiệu ─────────────────────────
export function IconXemThem({ className }: NavIconProps) {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" className={className}>
      <rect
        x="3.4"
        y="3.4"
        width="6.4"
        height="6.4"
        rx="1.8"
        stroke="currentColor"
        strokeWidth="1.45"
      />
      <circle cx="17.4" cy="6.6" r="3.2" stroke="currentColor" strokeWidth="1.45" />
      <path
        d="m6.6 14.1 3.5 6.1h-7l3.5-6.1Z"
        stroke="currentColor"
        strokeWidth="1.45"
        strokeLinejoin="round"
      />
      <rect
        x="14.2"
        y="14.2"
        width="6.4"
        height="6.4"
        rx="1.8"
        stroke="currentColor"
        strokeWidth="1.45"
      />
    </svg>
  );
}
